import React, { useEffect, useState } from 'react';
import {
  Grid,
  TextField,
  Paper,
  Button,
  Typography,
  TableRow,
  TableCell,
  InputAdornment,
  Autocomplete
} from '@mui/material';

import Table from '../../../components/Table';

import SearchIcon from '@mui/icons-material/Search';
import { api } from '../../../api';
import { Company } from '../../../schema/company';
import { useParams } from 'react-router-dom';

const personHead = ['เลขบัตรประชาชน', 'ชื่อ นามสกุล', 'ประเภท', 'บริษัท'];

const OnlinePersonTable: React.FC = () => {
  const { gate_group_id } = useParams();
  const [filterID, setFilterID] = useState<string>('');
  const [filterName, setFilterName] = useState<string>('');
  const [filterCompanyIDList, setFilterCompanyIDList] = useState<number[]>([]);
  const [filterCompanyList, setFilterCompanyList] = useState<Company[]>([]);
  const [filterPersonTypeIDList, setFilterPersonTypeIDList] = useState<string[] | number[]>([]);
  const [companySearchKeyword, setCompanySearchKeyword] = useState<string>('');

  const [companies, setCompanies] = useState<Company[]>([]);

  const [onlinePersons, setOnlinePersons] = useState<{
    total: number;
    limit: number;
    offset: number;
    data: [
      {
        id: number;
        company_id: number;
        email: string;
        employee_code: string;
        first_name: string;
        last_name: string;
        title_name: string;
        phone_number: string;
        personnal_id: string;
        person_type: any;
        company: any;
      }
    ];
  }>({
    total: 0,
    limit: 0,
    offset: 0,
    data: [
      {
        id: 0,
        company_id: 0,
        email: '',
        employee_code: '',
        first_name: '',
        last_name: '',
        title_name: '',
        phone_number: '',
        personnal_id: '',
        person_type: {},
        company: {}
      }
    ]
  });

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const [isLoading, setIsLoading] = useState<boolean>(false);

  const [isExportLoading, setIsExportLoading] = useState<boolean>(false);

  const handlePageChange = (_: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(+event.target.value);
    setPage(0);
  };

  const handleExportCSVFile = () => {
    if (isExportLoading) {
      return;
    }

    const searchParams = new URLSearchParams();

    if (filterID) {
      searchParams.set('personnal_id', filterID);
    }
    if (filterName) {
      searchParams.set('name', filterName);
    }
    if (filterCompanyIDList.length) {
      filterCompanyIDList.forEach((company_id) => {
        searchParams.append('company_id', `${company_id}`);
      });
    }
    if (filterPersonTypeIDList.length) {
      filterPersonTypeIDList.forEach((person_type_id) => {
        searchParams.append('person_type_id', `${person_type_id}`);
      });
    }
    setIsExportLoading(true);
    api
      .get(`/gate-group/${gate_group_id}/persons/online/export`, {
        params: searchParams,
        responseType: 'blob'
      })
      .then((response) => {
        const today = new Date();
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute(
          'download',
          'gate_group_' +
            gate_group_id +
            '_person_export_data_' +
            today.toLocaleDateString('en-US') +
            '.xlsx'
        );
        document.body.appendChild(link);
        link.click();
      })
      .finally(() => setIsExportLoading(false));
  };

  useEffect(() => {
    if (companySearchKeyword) {
      const timeOut = setTimeout(() => {
        const searchParams = new URLSearchParams();
        searchParams.set('company_name', companySearchKeyword);
        searchParams.set('limit', '25');

        api.get<Company[]>(`/company`, { params: searchParams }).then((res) => {
          const { data } = res;
          setCompanies(data);
        });
      }, 500);
      return () => clearTimeout(timeOut);
    } else {
      api
        .get<Company[]>(`/company`, {
          params: {
            limit: 25
          }
        })
        .then((res) => {
          const { data } = res;
          setCompanies(data);
        });
    }
  }, [companySearchKeyword]);

  useEffect(() => {
    setIsLoading(true);
    const timeOutId = setTimeout(() => {
      const searchParams = new URLSearchParams();

      if (filterID) {
        searchParams.set('personnal_id', filterID);
      }
      if (filterName) {
        searchParams.set('name', filterName);
      }
      if (filterCompanyIDList.length) {
        filterCompanyIDList.forEach((company_id) => {
          searchParams.append('company_id', `${company_id}`);
        });
      }
      if (filterPersonTypeIDList.length) {
        filterPersonTypeIDList.forEach((person_type_id) => {
          searchParams.append('person_type_id', `${person_type_id}`);
        });
      }
      searchParams.set('offset', `${page * rowsPerPage}`);
      searchParams.set('limit', `${rowsPerPage}`);

      api
        .get<any>(`/gate-group/${gate_group_id}/persons/online`, { params: searchParams })
        .then((res) => {
          const { data } = res;
          setOnlinePersons(data);
        })
        .finally(() => setIsLoading(false));
    }, 500);
    return () => clearTimeout(timeOutId);
  }, [filterID, filterName, filterCompanyIDList, filterPersonTypeIDList, page, rowsPerPage]);

  return (
    <Paper sx={{ mt: 3 }}>
      <Typography variant="h6" sx={{ px: 2, py: 2 }}>
        {'บุคคลที่อยู่ในกลุ่มประตูในปัจจุบัน'}
        <Button
          sx={{ ml: 2 }}
          variant="outlined"
          color="primary"
          onClick={handleExportCSVFile}
          disabled={isExportLoading}
        >
          Export
        </Button>
      </Typography>
      <Grid container spacing={2} sx={{ padding: 2 }}>
        <Grid item md={3}>
          <TextField
            id="search-text-label"
            label="เลขบัตรประชาชน"
            variant="outlined"
            placeholder="เลขบัตรประชาชน"
            onChange={(event) => setFilterID(event.target.value)}
            fullWidth
            value={filterID}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              )
            }}
            autoComplete="off"
          />
        </Grid>
        <Grid item md={3}>
          <TextField
            id="search-text-label"
            label="ค้นหาชื่อ - นามสกุล"
            variant="outlined"
            placeholder="ค้นหาชื่อ - นามสกุล"
            value={filterName}
            onChange={(event) => setFilterName(event.target.value)}
            fullWidth
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              )
            }}
          />
        </Grid>
        {/* <Grid item md={3}>
        <MultiSelect
          options={personTypes.map((data) => ({ id: data.id.toString(), name: data.type_name }))}
          onChange={(data) => setFilterPersonTypeIDList(data)}
          label="ประเภทบุคคล"
        />
      </Grid> */}
        <Grid item md={3}>
          <Autocomplete
            multiple
            limitTags={2}
            disablePortal
            options={companies}
            getOptionLabel={(option) => (option?.company_name ? option.company_name : '')}
            renderInput={(params) => <TextField {...params} label="บริษัท" placeholder="บริษัท" />}
            onChange={(_, value) => {
              setCompanySearchKeyword('');
              setFilterCompanyIDList(value.map((data) => data.id));
              setFilterCompanyList(value);
            }}
            value={filterCompanyList}
            onInputChange={(_, newInputValue) => {
              setCompanySearchKeyword(newInputValue);
            }}
            isOptionEqualToValue={(option, v) => v?.id == option?.id}
            fullWidth
          />
        </Grid>
      </Grid>
      <Grid container spacing={2} sx={{ padding: 2 }}>
        <Grid item xs={12}>
          <Table
            heads={personHead}
            count={onlinePersons.total}
            page={page}
            rowsPerPage={rowsPerPage}
            onPageChange={handlePageChange}
            onRowsPerPageChange={handleRowsPerPageChange}
            isLoading={isLoading}
          >
            {onlinePersons.data.length <= 0 && (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ p: 1 }}>
                  <Typography>ไม่พบข้อมูลรายการบุคคล</Typography>
                </TableCell>
              </TableRow>
            )}
            {onlinePersons.data.map((data, index) => (
              <TableRow key={index}>
                <TableCell align="center">
                  {data.employee_code ? data.employee_code : data.personnal_id}
                </TableCell>
                <TableCell align="center">
                  {data.title_name + ' ' + data.first_name + ' ' + data.last_name}
                </TableCell>
                <TableCell align="center">{data.person_type?.type_name}</TableCell>
                <TableCell align="center">{data.company?.company_name}</TableCell>
              </TableRow>
            ))}
          </Table>
        </Grid>
      </Grid>
    </Paper>
  );
};
export default OnlinePersonTable;
